// React Hooks:
import React, { useEffect } from "react";
// React-Router-Dom:
import { useNavigate } from "react-router-dom";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  drawerOpenOrCloseAction,
  drawerItemOpenAndCloseAction,
  drawerItemOpenOrCloseAction,
  drawer,
} from "../drawerSlice";
import { directoryItems, directoryItemsTouchedAction } from "../../directory/directory-items/directoryItemsSlice";
import { shareManagement } from "../../../share-management/shareManagementSlice";
// MUI Components:
import {
  ListItem,
  ListItemButton,
  ListItemText,
  SvgIcon,
  Collapse,
  Divider,
  useTheme,
} from "@mui/material";
// Components:
import DrawerInfo from "./drawer-info/DrawerInfo";
import DrawerShare from "./drawer-share/DrawerShare";
// MUI Styles:
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  active: {
    backgroundColor: `${theme.palette.secondary.main} !important`,
    color: `${theme.palette.secondary.contrastText} !important`,
    "&:hover": {
      filter: "brightness(95%) !important",
    },
  },
}));

// Type Controller
interface DrawerItemProps {
  item: {
    name: string;
    icon: any;
  };
  index: number;
}

const DrawerItem: React.FC<DrawerItemProps> = ({ item, index }) => {
  const classes = useStyles();
  const theme = useTheme();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { openDrawer, drawerItem } = useAppSelector(drawer);
  const { itemInfo } = useAppSelector(directoryItems);
  const {
    shareManagementData: { list },
  } = useAppSelector(shareManagement);

  useEffect(() => {
    if (openDrawer === false) {
      dispatch(drawerItemOpenOrCloseAction({ name: "", open: false }));
    }
  }, [dispatch, openDrawer]);

  return (
    <ListItem
      disablePadding
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ListItemButton
        disabled={
          item.name === "share management" && !list?.length ? true : false
        }
        sx={{
          width: "100%",
          height: "50px",
          display: "flex",
          justifyContent: openDrawer ? "flex-start" : "center",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "inherit",
          borderRadius: "5px",
          transition: "all 0.4s",
          p: "4px 8px",
          m: "0px 0px 6px 0px ",
          "&:hover": {
            filter: "brightness(95%)",
          },
          [theme.breakpoints.down("lg")]: {
            height: "45px",
          },
          [theme.breakpoints.down("md")]: {
            height: "40px",
          },
        }}
        className={
          drawerItem.name === item.name && drawerItem.open ? classes.active : ""
        }
        onClick={() => {
          if (item.name !== "share management") {
            dispatch(drawerOpenOrCloseAction(true));
            dispatch(drawerItemOpenAndCloseAction(item.name));
          } else {
            dispatch(directoryItemsTouchedAction(false));
            dispatch(drawerOpenOrCloseAction(false));
            navigate("/share-management");
          }
        }}
      >
        <SvgIcon
          component={item.icon}
          inheritViewBox
          sx={{
            width: "18px",
            height: "18px",
            color: "inherit",
          }}
        />

        {openDrawer && (
          <ListItemText
            primary={item.name}
            sx={{
              ml: "8px",
              fontSize: "1rem",
            }}
          />
        )}
      </ListItemButton>

      <Collapse
        in={drawerItem.name === item.name && drawerItem.open}
        sx={{ width: "100%" }}
      >
        {item.name === "info" && <DrawerInfo itemInfo={itemInfo} />}

        {item.name === "share" && <DrawerShare itemInfo={itemInfo} />}

        {item.name !== "share management" && (
          <Divider sx={{ opacity: "0.2", mt: "8px", mb: "8px" }} />
        )}
      </Collapse>
    </ListItem>
  );
};

export default DrawerItem;
